import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { saveShippingAddress } from "../Redux/Actions/cartActions";
import { guestLogin } from "../Redux/Actions/guestActions";
import URL from "../Redux/Url";
import { COUNTRY_DATA } from "../screens/countrydata";
import TypeaheadSelect from "../screens/typeheadselect";
import { usePostalLookup } from "../screens/usepostallookup";

const ZIP_ICON    = { idle: "", loading: "◌", found: "✦", notfound: "✕", error: "" };
const ZIP_MESSAGE = {
  idle:     { text: "",                             cls: "sf-zip-status--hidden" },
  loading:  { text: "",                             cls: "sf-zip-status--hidden" },
  found:    { text: "✦  City & state auto-filled",  cls: "sf-zip-status--found"  },
  notfound: { text: "—  Not found · fill manually", cls: "sf-zip-status--error"  },
  error:    { text: "",                             cls: "sf-zip-status--hidden" },
};

const ShippingScreen = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo }  = useSelector((state) => state.userLogin);
  const guest         = useSelector((state) => state.guest);
  const isGuest       = guest?.isGuest && !userInfo;

  const [guestEmail, setGuestEmail]               = useState(guest?.email || "");
  const [street1, setStreet]                      = useState("");
  const [city, setCity]                           = useState("");
  const [state, setState]                         = useState("");
  const [zipcode, setZipcode]                     = useState("");
  const [country, setCountry]                     = useState("US");
  const [validationResult, setValidationResult]   = useState(null);
  const [isLoading, setIsLoading]                 = useState(false);
  const [isFetching, setIsFetching]               = useState(true);
  const [autoFilled, setAutoFilled]               = useState(false);
  const [savedAddress, setSavedAddress]           = useState(null);
  const [lastAutoFilledZip, setLastAutoFilledZip] = useState("");

  const countryOptions = Object.entries(COUNTRY_DATA).map(([code, data]) => ({
    code, name: data.name, flag: data.flag,
  }));

  const currentCountryData = COUNTRY_DATA[country] || COUNTRY_DATA.US;
  const stateOptions = currentCountryData.states;

  const zipStatus = usePostalLookup({
    zipcode,
    country,
    onResult: ({ city: c, state: s }) => {
      setCity(c);
      setState(s);
      setAutoFilled(true);
      setLastAutoFilledZip(zipcode);
    },
  });

  const handleCountryChange = (code) => {
    setCountry(code);
    setState(""); setZipcode(""); setCity("");
    setAutoFilled(false); setLastAutoFilledZip("");
    setValidationResult(null);
  };

  const handleZipChange = (e) => {
    const val = e.target.value;
    setZipcode(val);
    if (val !== lastAutoFilledZip) setAutoFilled(false);
  };

  // Pre-fill saved address for logged-in users only
  useEffect(() => {
    const fetchShippingAddress = async () => {
      if (!userInfo?.token) { setIsFetching(false); return; }
      try {
        const res = await fetch(`${URL}/api/customers/profile`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch profile");

        const addr = data.shippingAddress;
        if (addr?.street1 && addr?.city && addr?.state && addr?.zipcode) {
          setSavedAddress(addr);
          setStreet(addr.street1);
          setCity(addr.city);
          setState(addr.state);
          setZipcode(addr.zipcode);
          setCountry(addr.country || "US");
          setLastAutoFilledZip(addr.zipcode);
        }
      } catch (err) {
        console.error("Error fetching shipping address:", err.message);
      } finally {
        setIsFetching(false);
      }
    };
    fetchShippingAddress();
  }, [userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();
    setValidationResult(null);

    if (!street1 || !city || !state || !zipcode) {
      setValidationResult("All fields are required.");
      return;
    }
    if (!currentCountryData.zipPattern.test(zipcode)) {
      setValidationResult(`Invalid ${currentCountryData.zipLabel}.`);
      return;
    }

    if (isGuest) {
      if (!guestEmail.trim()) {
        setValidationResult("Please enter your email address.");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
        setValidationResult("Please enter a valid email address.");
        return;
      }
      dispatch(guestLogin(guestEmail.trim()));
      dispatch(saveShippingAddress({ street1, city, state, zipcode, country }));
      navigate("/payment");
      return;
    }

    // Logged-in user flow
    setIsLoading(true);
    try {
      const res = await fetch(`${URL}/api/customers/shipping-address`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({ shippingAddress: { street1, city, state, zipcode, country } }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Something went wrong. Try again.");

      dispatch(saveShippingAddress({ userId: userInfo._id, street1, city, state, zipcode, country }));
      navigate("/payment");
    } catch (error) {
      console.error("Error:", error.message);
      setValidationResult(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <>
        <Header />
        <div className="container d-flex justify-content-center align-items-center login-center">
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.2em", color: "#7a7a85" }}>
            LOADING···
          </div>
        </div>
      </>
    );
  }

  const zipMsg = autoFilled && zipStatus === "found"
    ? ZIP_MESSAGE.found
    : ZIP_MESSAGE[zipStatus] || ZIP_MESSAGE.idle;

  return (
    <>
      <Header />
      <div className="container d-flex justify-content-center align-items-center login-center"
           style={{ minHeight: "80vh", padding: "40px 16px" }}>
        <div className="sf-shell">

          <div className="sf-step"><span>01</span> / 03</div>
          <h2 className="sf-heading">Delivery</h2>
          <p className="sf-subheading">Shipping address</p>
          <hr className="sf-divider" />

          {isGuest && (
            <div className="sf-notice" style={{ marginBottom: "1rem" }}>
              <div className="sf-notice-dot" />
              Checking out as guest — enter your email for order confirmation
            </div>
          )}

          {savedAddress && !isGuest && (
            <div className="sf-notice">
              <div className="sf-notice-dot" />
              Saved address pre-filled — update if needed
            </div>
          )}

          <form onSubmit={submitHandler}>

            {isGuest && (
              <div className="sf-field">
                <label className="sf-field-label" htmlFor="sf-guest-email">
                  Email address
                </label>
                <input
                  id="sf-guest-email"
                  className="sf-input"
                  type="email"
                  placeholder="you@example.com"
                  value={guestEmail}
                  required
                  onChange={(e) => setGuestEmail(e.target.value)}
                />
              </div>
            )}

            <div className="sf-field">
              <span className="sf-field-label">Country</span>
              <TypeaheadSelect
                options={countryOptions}
                value={country}
                onChange={handleCountryChange}
                placeholder="Select country"
                icon="🌍"
              />
            </div>

            <div className="sf-field">
              <label className="sf-field-label" htmlFor="sf-street">Street address</label>
              <input
                id="sf-street"
                className="sf-input"
                type="text"
                placeholder="123 Main Street"
                value={street1}
                required
                onChange={(e) => setStreet(e.target.value)}
              />
            </div>

            <div className="sf-row">
              <div className="sf-field">
                <label className="sf-field-label" htmlFor="sf-zip">
                  {currentCountryData.zipLabel}
                </label>
                <div className="sf-zip-row">
                  <input
                    id="sf-zip"
                    className="sf-input"
                    type="text"
                    placeholder={currentCountryData.zipPlaceholder}
                    value={zipcode}
                    required
                    onChange={handleZipChange}
                  />
                  <span className="sf-zip-icon">{ZIP_ICON[zipStatus]}</span>
                </div>
                <div className={`sf-zip-status ${zipMsg.cls}`}>{zipMsg.text}</div>
              </div>

              <div className="sf-field">
                <label className="sf-field-label" htmlFor="sf-city">City</label>
                <input
                  id="sf-city"
                  className="sf-input"
                  type="text"
                  placeholder="City"
                  value={city}
                  required
                  onChange={(e) => { setCity(e.target.value); setAutoFilled(false); }}
                />
              </div>
            </div>

            <div className="sf-field">
              <span className="sf-field-label">{currentCountryData.stateLabel}</span>
              <TypeaheadSelect
                options={stateOptions}
                value={state}
                onChange={setState}
                placeholder={`Select ${currentCountryData.stateLabel}`}
                locked={autoFilled && !!state}
              />
            </div>

            <div className="sf-error-slot">
              {validationResult && <div className="sf-error">{validationResult}</div>}
            </div>

            <button type="submit" disabled={isLoading} className="sf-btn">
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span>Processing</span>
                </>
              ) : (
                <>
                  <span>Continue to Payment</span>
                  <span className="sf-btn-arrow">→</span>
                </>
              )}
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default ShippingScreen;